import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Inbox } from "lucide-react";
import { AdminLogin } from "../components/admin/AdminLogin";
import { AdminToolbar } from "../components/admin/AdminToolbar";
import { AdminFilters } from "../components/admin/AdminFilters";
import { LeadRow } from "../components/admin/LeadRow";
import { BookingDialog } from "../components/admin/BookingDialog";
import { NotesDialog } from "../components/admin/NotesDialog";
import { PaymentsList } from "../components/admin/PaymentsList";
import { MembersList } from "../components/admin/MembersList";
import { LeadManageDialog } from "../components/admin/LeadManageDialog";
import { ScheduleDialog } from "../components/admin/ScheduleDialog";
import { CustomersTab } from "../components/admin/CustomersTab";
import { AppointmentsTab } from "../components/admin/AppointmentsTab";
import { RecurringTab } from "../components/admin/RecurringTab";
import { TechsTab } from "../components/admin/TechsTab";
import { EstimatesTab } from "../components/admin/EstimatesTab";
import { ActivityTab } from "../components/admin/ActivityTab";
import { SettingsTab } from "../components/admin/SettingsTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HTTP_UNAUTHORIZED = 401;
const SEARCH_DEBOUNCE_MS = 300;

const TABS = [
  ["leads", "Leads"],
  ["customers", "Customers"],
  ["appointments", "Appointments"],
  ["recurring", "Recurring"],
  ["estimates", "Estimates"],
  ["techs", "Techs"],
  ["payments", "Payments"],
  ["members", "Members"],
  ["activity", "Activity"],
  ["settings", "Settings"],
];

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [notesLead, setNotesLead] = useState(null);
  const [manageLead, setManageLead] = useState(null);
  const [scheduleLead, setScheduleLead] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [view, setView] = useState("leads");
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/me`)
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, [setAuthed]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/admin/logout`);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("Logout request failed; clearing local auth state anyway:", err);
    }
    setAuthed(false);
    setLeads([]);
  }, [setAuthed, setLeads]);

  const handleAuthError = useCallback((err) => {
    if (err.response?.status === HTTP_UNAUTHORIZED) {
      toast.error("Session expired. Please sign in again.");
      setAuthed(false);
      setLeads([]);
      return true;
    }
    return false;
  }, [setAuthed, setLeads]);

  const fetchLeads = useCallback(async () => {
    if (!authed) return;
    try {
      const res = await axios.get(`${API}/leads`, {
        params: { search: search || undefined, status: statusFilter },
      });
      setLeads(res.data);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, authed, handleAuthError, setLeads, setLoading]);

  useEffect(() => {
    const timer = setTimeout(fetchLeads, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [fetchLeads]);

  useEffect(() => {
    if (!authed || view !== "payments") return;
    setPaymentsLoading(true);
    axios.get(`${API}/payments`)
      .then((res) => setPayments(res.data))
      .catch((err) => { if (!handleAuthError(err)) toast.error("Failed to load payments"); })
      .finally(() => setPaymentsLoading(false));
  }, [authed, view, handleAuthError]);

  useEffect(() => {
    if (!authed || view !== "members") return;
    setMembersLoading(true);
    axios.get(`${API}/admin/members`)
      .then((res) => setMembers(res.data))
      .catch((err) => { if (!handleAuthError(err)) toast.error("Failed to load members"); })
      .finally(() => setMembersLoading(false));
  }, [authed, view, handleAuthError]);

  const updateLead = async (id, updates) => {
    try {
      const res = await axios.patch(`${API}/leads/${id}`, updates);
      setLeads((prev) => prev.map((l) => (l.id === id ? res.data : l)));
      toast.success("Lead updated");
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Update failed");
    }
  };

  const onLeadUpdated = (updated) => {
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    setManageLead(updated);
  };

  const saveNotes = async (notes) => {
    await updateLead(notesLead.id, { notes });
    setNotesLead(null);
  };

  const saveBooking = async (payload) => {
    try {
      const res = await axios.post(`${API}/leads/manual`, payload);
      setLeads((prev) => [res.data, ...prev]);
      setBookingOpen(false);
      toast.success("Calendar booking logged");
      return true;
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Failed to log booking");
      return false;
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/leads/export/csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "masons-leads.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Export failed");
    }
  };

  const renderLeads = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>
      );
    }
    if (leads.length === 0) {
      return (
        <div data-testid="admin-empty-state" className="text-center py-24 text-zinc-500">
          <Inbox className="h-12 w-12 mx-auto mb-4 text-zinc-700" />
          No leads found.
        </div>
      );
    }
    return (
      <div className="space-y-3" data-testid="admin-leads-list">
        {leads.map((lead) => (
          <LeadRow
            key={lead.id}
            lead={lead}
            onStatusChange={(id, status) => updateLead(id, { status })}
            onOpenNotes={setNotesLead}
            onManage={setManageLead}
          />
        ))}
      </div>
    );
  };

  if (authed === null) {
    return (
      <main data-testid="admin-page" className="pt-20 md:pt-24 min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </main>
    );
  }

  if (!authed) {
    return (
      <main data-testid="admin-page" className="pt-20 md:pt-24 min-h-screen">
        <AdminLogin onLogin={() => { setLoading(true); setAuthed(true); }} />
      </main>
    );
  }

  return (
    <main data-testid="admin-page" className="pt-20 md:pt-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-10">
        <AdminToolbar
          leadCount={leads.length}
          onLogBooking={() => setBookingOpen(true)}
          onExport={exportCsv}
          onLogout={logout}
        />
        <div className="flex gap-2 mb-6 flex-wrap" data-testid="admin-view-tabs">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              data-testid={`tab-${key}`}
              onClick={() => setView(key)}
              className={`rounded-sm px-5 py-2 text-sm font-semibold transition-colors ${view === key ? "bg-blue-600 text-white" : "border border-zinc-800 text-zinc-400 hover:text-white"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {view === "leads" && (
          <AdminFilters
            search={search}
            onSearch={setSearch}
            statusFilter={statusFilter}
            onStatusFilter={setStatusFilter}
          />
        )}

        {view === "leads" && renderLeads()}
        {view === "customers" && <CustomersTab onAuthError={handleAuthError} />}
        {view === "appointments" && <AppointmentsTab onAuthError={handleAuthError} />}
        {view === "recurring" && <RecurringTab onAuthError={handleAuthError} />}
        {view === "estimates" && <EstimatesTab onAuthError={handleAuthError} />}
        {view === "techs" && <TechsTab onAuthError={handleAuthError} />}
        {view === "payments" && <PaymentsList payments={payments} loading={paymentsLoading} />}
        {view === "members" && <MembersList members={members} loading={membersLoading} />}
        {view === "activity" && <ActivityTab onAuthError={handleAuthError} />}
        {view === "settings" && <SettingsTab onAuthError={handleAuthError} />}
      </div>

      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} onSubmit={saveBooking} />
      <NotesDialog lead={notesLead} onClose={() => setNotesLead(null)} onSave={saveNotes} />
      {manageLead && (
        <LeadManageDialog
          lead={manageLead}
          onClose={() => setManageLead(null)}
          onUpdated={onLeadUpdated}
          onSchedule={(lead) => setScheduleLead(lead)}
        />
      )}
      {scheduleLead && (
        <ScheduleDialog
          key={`lead-sched-${scheduleLead.id}`}
          open
          onOpenChange={(v) => !v && setScheduleLead(null)}
          context={{
            lead_id: scheduleLead.id,
            name: scheduleLead.name,
            phone: scheduleLead.phone,
            service_needed: scheduleLead.service_needed,
          }}
          onSaved={() => { setScheduleLead(null); fetchLeads(); toast.info("Appointment visible in the Appointments tab"); }}
        />
      )}
    </main>
  );
}
